import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Mail, Send, Trash2, UserPlus } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label, TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";

type ErrorModel = components["schemas"]["ErrorModel"];
type InvitesResponse = components["schemas"]["UserInviteListResponse"];
type Organization = components["schemas"]["OrganizationDTO"];

/** Platform roles (user.role) offered when no organization is selected. */
const PLATFORM_ROLES = ["staff", "support", "admin", "read_only"] as const;
/** Organization roles (member.role) offered when an organization is selected. */
const ORG_ROLES = ["org_admin", "campus_admin", "teacher", "staff", "student", "guardian"] as const;

const PLATFORM_ORG = "__platform__";

function statusVariant(status: string): "secondary" | "outline" | "default" {
  if (status === "accepted") {
    return "secondary";
  }
  if (status === "expired") {
    return "outline";
  }
  return "default";
}

/**
 * F2-C — platform invitations manager (list / create / revoke). An invite
 * pre-binds a role: a platform role when no org is picked, or an org membership
 * (role + campus) otherwise. The emailed link lands in signup, which applies the
 * binding on register.
 */
export function InvitesManager() {
  const { t } = useI18n();
  const { can } = useAdminCapabilities();

  const [email, setEmail] = createSignal("");
  const [orgId, setOrgId] = createSignal<string>(PLATFORM_ORG);
  const [role, setRole] = createSignal<string>("staff");
  const [campusId, setCampusId] = createSignal("");

  const invitesQuery = createApiQuery<InvitesResponse, ErrorModel>(
    () => ["admin", "invites"],
    async () => api.GET("/api/admin/invites", {}),
    () => ({ staleTime: 10_000 }),
  );
  const orgsQuery = createApiQuery<Organization[], ErrorModel>(
    () => ["org", "list"],
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const isPlatform = () => orgId() === PLATFORM_ORG;
  const roleOptions = createMemo(() =>
    (isPlatform() ? PLATFORM_ROLES : ORG_ROLES).map((r) => ({ value: r, label: r })),
  );
  const orgOptions = createMemo(() => [
    { value: PLATFORM_ORG, label: t(msg`Platform (no organization)`) },
    ...(orgsQuery.data ?? []).map((o) => ({ value: o.id, label: o.name })),
  ]);

  const createMutation = createApiMutation(
    async (vars: { email: string; role: string; organizationId?: string; campusId?: string }) =>
      api.POST("/api/admin/invites", { body: vars }),
  );
  const revokeMutation = createApiMutation(async (vars: { id: string }) =>
    api.DELETE("/api/admin/invites/{id}", { params: { path: { id: vars.id } } }),
  );

  const submit = () => {
    if (!email().trim() || !role()) {
      return;
    }
    createMutation.mutate(
      {
        email: email().trim(),
        role: role(),
        organizationId: isPlatform() ? undefined : orgId(),
        campusId: isPlatform() ? undefined : campusId().trim() || undefined,
      },
      {
        onSuccess: () => {
          setEmail("");
          setCampusId("");
          void invitesQuery.refetch();
          showToast(t(msg`Invitation sent`), "success");
        },
        onError: () => showToast(t(msg`Could not create invitation`), "error"),
      },
    );
  };

  const revoke = (id: string) => {
    revokeMutation.mutate(
      { id },
      {
        onSuccess: () => {
          void invitesQuery.refetch();
          showToast(t(msg`Invitation revoked`), "success");
        },
      },
    );
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <Mail class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Invitations`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(msg`Invite people with a pre-bound role and organization.`)}
          </p>
        </div>
      </div>

      <Show when={can("users.invite")}>
        <Card class="bg-primary/5 border-primary/10">
          <CardContent class="p-4 space-y-3">
            <div class="grid gap-3 sm:grid-cols-2">
              <div class="sm:col-span-2">
                <Label for="invite-email">{t(msg`Email`)}</Label>
                <TextField
                  id="invite-email"
                  type="email"
                  value={email()}
                  placeholder={t(msg`person@example.com`)}
                  onInput={(e) => setEmail(e.currentTarget.value)}
                />
              </div>
              <div>
                <Label>{t(msg`Organization`)}</Label>
                <Select<string>
                  aria-label={t(msg`Organization`)}
                  value={orgId()}
                  options={orgOptions()}
                  onChange={(v) => {
                    const next = v ?? PLATFORM_ORG;
                    setOrgId(next);
                    // Reset the role to a valid one for the new scope.
                    setRole(next === PLATFORM_ORG ? "staff" : "teacher");
                  }}
                />
              </div>
              <div>
                <Label>{t(msg`Role`)}</Label>
                <Select<string>
                  aria-label={t(msg`Role`)}
                  value={role()}
                  options={roleOptions()}
                  onChange={(v) => setRole(v ?? "")}
                />
              </div>
              <Show when={!isPlatform()}>
                <div class="sm:col-span-2">
                  <Label for="invite-campus">{t(msg`Campus ID (optional)`)}</Label>
                  <TextField
                    id="invite-campus"
                    value={campusId()}
                    placeholder={t(msg`scope the membership to a campus`)}
                    onInput={(e) => setCampusId(e.currentTarget.value)}
                  />
                </div>
              </Show>
            </div>
            <Button
              size="sm"
              disabled={createMutation.isPending || !email().trim()}
              onClick={submit}
              data-testid="create-invite"
            >
              <Send class="mr-1.5 h-4 w-4" />
              {t(msg`Send invitation`)}
            </Button>
          </CardContent>
        </Card>
      </Show>

      <Show when={!invitesQuery.isLoading} fallback={<Skeleton class="h-24 w-full rounded-xl" />}>
        <div class="space-y-2">
          <For
            each={invitesQuery.data?.data ?? []}
            fallback={
              <Card>
                <CardContent class="p-8 text-center text-sm text-muted-foreground">
                  {t(msg`No invitations yet.`)}
                </CardContent>
              </Card>
            }
          >
            {(inv) => (
              <Card>
                <CardContent class="p-4 flex items-center justify-between gap-3">
                  <div class="min-w-0 flex items-center gap-3">
                    <UserPlus class="h-5 w-5 text-muted-foreground shrink-0" />
                    <div class="min-w-0">
                      <div class="font-medium truncate">{inv.email}</div>
                      <div class="text-xs text-muted-foreground">
                        <span class="capitalize">{inv.role}</span>
                        {inv.organizationId ? ` · ${t(msg`org`)}` : ` · ${t(msg`platform`)}`}
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <Badge variant={statusVariant(inv.status)} class="capitalize">
                      {inv.status}
                    </Badge>
                    <Show when={inv.status === "pending" && can("users.invite")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-destructive hover:text-destructive"
                        onClick={() => revoke(inv.id)}
                      >
                        <Trash2 class="h-4 w-4" />
                      </Button>
                    </Show>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}
